import React, { useState, useMemo, useEffect } from 'react';
import { useLang } from '../../core/lang';
import LangKeys from '../../core/locales/keys';
import billService from '../../services/billService';
import itemService from '../../services/itemService';
import {
  ArrowLeft,
  Download,
  Share2,
  ChevronDown,
  Search,
  Package,
  Zap,
  ChevronRight
} from 'lucide-react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  }).format(amount || 0);
};

const formatDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  return `${d.getDate().toString().padStart(2, '0')}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getFullYear()}`;
};

const toInputDateFormat = (date) => {
  if (!date) return '';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ItemReportView = ({ onBack }) => {
  const { t } = useLang();
  const [selectedRange, setSelectedRange] = useState('this_month');
  const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [toDate, setToDate] = useState(new Date());
  
  const [activeTab, setActiveTab] = useState('product'); // 'product' or 'service'
  const [bills, setBills] = useState([]);
  const [itemsList, setItemsList] = useState([]);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadReportData = async () => {
      setIsLoading(true);
      try {
        const [billsData, itemsData] = await Promise.all([
          billService.fetchBills(),
          itemService.getAllItems()
        ]);
        setBills(billsData || []);
        setItemsList(itemsData || []);
        
        const { getBusinessProfile } = await import('../../services/dashboardApi');
        const res = await getBusinessProfile();
        if (res?.success) setBusinessProfile(res.data);
      } catch (err) {
        console.error('Error loading report data:', err);
      }
      setIsLoading(false);
    };
    loadReportData();
  }, []);

  const itemTypeMap = useMemo(() => {
    const map = {};
    itemsList.forEach(item => {
      if (item.id) {
        map[item.id.toString()] = item.item_type || item.itemType || 'product';
      }
    });
    return map;
  }, [itemsList]);

  const itemPurchasePriceMap = useMemo(() => {
    const map = {};
    itemsList.forEach(item => {
      if (item.id) {
        map[item.id.toString()] = parseFloat(item.purchase_price || 0.0);
      }
    });
    return map;
  }, [itemsList]);

  const dateRanges = [
    { value: 'custom', label: t(LangKeys.date_range) },
    { value: 'today', label: t(LangKeys.single_day) },
    { value: 'this_month', label: t(LangKeys.this_month) },
    { value: 'last_week', label: t(LangKeys.last_week) },
    { value: 'last_month', label: t(LangKeys.last_month) },
    { value: 'all', label: t(LangKeys.all) },
  ];

  const handleFromDateChange = (e) => {
    const val = e.target.value;
    if (val) {
      const d = new Date(val);
      if (!isNaN(d.getTime())) {
        setFromDate(d);
        setSelectedRange('custom');
      }
    }
  };

  const handleToDateChange = (e) => {
    const val = e.target.value;
    if (val) {
      const d = new Date(val);
      if (!isNaN(d.getTime())) {
        setToDate(d);
        setSelectedRange('custom');
      }
    }
  };

  const applyDateRange = (type) => {
    if (type === 'custom') {
      setSelectedRange('custom');
      return;
    }
    const now = new Date();
    let from, to;
    switch (type) {
      case 'today':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'last_week':
        from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        to = now;
        break;
      case 'this_month':
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);
        break;
      case 'last_month':
        from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        to = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        break;
      case 'all':
        from = new Date(2020, 0, 1);
        to = now;
        break;
      default:
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    }
    setFromDate(from);
    setToDate(to);
    setSelectedRange(type);
  };

  // Exact Flutter mobile FIFO algorithm for Item-Wise Report
  const reportData = useMemo(() => {
    const fromDateStart = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
    const toDateEnd = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate(), 23, 59, 59);

    const filterType = activeTab; // 'product' or 'service'

    // 1. Sort all bills chronologically to process FIFO
    const sortedBills = [...bills].sort((a, b) => new Date(a.date) - new Date(b.date));

    // FIFO Queues
    const fifoQueues = {};

    // Dynamic current stocks across all time
    const currentStocks = {};
    itemsList.forEach(item => {
      const itType = item.item_type || item.itemType || 'product';
      if (itType === filterType) {
        const itemId = String(item.id || item.name);
        currentStocks[itemId] = parseFloat(item.opening_stock || item.openingStock || 0);
      }
    });

    bills.forEach(bill => {
      if (bill.type === 'expense') return;
      const bItems = Array.isArray(bill.items) ? bill.items : [];
      bItems.forEach(billItem => {
        const id = String(billItem.item?.id || billItem.item_id || billItem.id || billItem.itemName || billItem.name || 'unknown');
        let itType = itemTypeMap[id] || billItem.item?.item_type || billItem.item?.itemType || billItem.itemType || 'product';
        if (itType === 'item') itType = 'product';
        if (itType !== filterType) return;

        if (currentStocks[id] !== undefined) {
          const qty = parseFloat(billItem.quantity || 0);
          if (bill.type === 'sale') {
            currentStocks[id] -= qty;
          } else if (bill.type === 'purchase') {
            currentStocks[id] += qty;
          }
        }
      });
    });

    // Initialize FIFO queues with opening stock of items
    itemsList.forEach(item => {
      const itType = item.item_type || item.itemType || 'product';
      const opStock = parseFloat(item.opening_stock || item.openingStock || 0);
      const pPrice = parseFloat(item.purchase_price || item.purchasePrice || 0);

      if (itType === filterType && opStock > 0) {
        const itemId = String(item.id || item.name);
        if (!fifoQueues[itemId]) {
          fifoQueues[itemId] = [];
        }
        fifoQueues[itemId].push({ qty: opStock, price: pPrice });
      }
    });

    // Track total metrics inside the date range
    const rangeSoldQty = {};
    const rangeGrossSalesAmount = {};
    const rangeNetSalesAmount = {};
    const rangeFifoCost = {};
    const rangePurchasedQty = {};
    const rangePurchaseAmount = {};

    // Opening stock if within range
    itemsList.forEach(item => {
      const itType = item.item_type || item.itemType || 'product';
      const opStock = parseFloat(item.opening_stock || item.openingStock || 0);
      const pPrice = parseFloat(item.purchase_price || item.purchasePrice || 0);

      if (itType === filterType && opStock > 0) {
        const itemId = String(item.id || item.name);
        const asOfDate = item.as_of_date || item.asOfDate ? new Date(item.as_of_date || item.asOfDate) : fromDateStart;
        if (asOfDate >= fromDateStart && asOfDate <= toDateEnd) {
          rangePurchasedQty[itemId] = (rangePurchasedQty[itemId] || 0) + opStock;
          rangePurchaseAmount[itemId] = (rangePurchaseAmount[itemId] || 0) + (opStock * pPrice);
        }
      }
    });

    // Process all bills chronologically up to toDateEnd
    sortedBills.forEach(bill => {
      const bDate = new Date(bill.date);
      if (bDate > toDateEnd) return;
      if (bill.type === 'expense') return;

      const isWithinRange = bDate >= fromDateStart && bDate <= toDateEnd;
      const bItems = Array.isArray(bill.items) ? bill.items : [];
      const billSubTotal = bItems.reduce((sum, item) => sum + (parseFloat(item.total || item.amount || 0)), 0);

      bItems.forEach(billItem => {
        const id = String(billItem.item?.id || billItem.item_id || billItem.id || billItem.itemName || billItem.name || 'unknown');
        let itType = itemTypeMap[id] || billItem.item?.item_type || billItem.item?.itemType || billItem.itemType || 'product';
        if (itType === 'item') itType = 'product';
        if (itType !== filterType) return;

        const qty = parseFloat(billItem.quantity || 0);
        const total = parseFloat(billItem.total || billItem.amount || 0);

        if (bill.type === 'purchase') {
          if (!fifoQueues[id]) {
            fifoQueues[id] = [];
          }
          const unitPrice = qty > 0 ? (total / qty) : 0;
          fifoQueues[id].push({ qty, price: unitPrice });

          if (isWithinRange) {
            rangePurchasedQty[id] = (rangePurchasedQty[id] || 0) + qty;
            rangePurchaseAmount[id] = (rangePurchaseAmount[id] || 0) + total;
          }
        } else if (bill.type === 'sale') {
          let remainingQty = qty;
          let saleCost = 0;
          const queue = fifoQueues[id] || [];

          while (remainingQty > 0 && queue.length > 0) {
            const batch = queue[0];
            if (batch.qty <= remainingQty) {
              saleCost += batch.qty * batch.price;
              remainingQty -= batch.qty;
              queue.shift();
            } else {
              saleCost += remainingQty * batch.price;
              batch.qty -= remainingQty;
              remainingQty = 0;
            }
          }

          if (remainingQty > 0) {
            const fallbackPrice = parseFloat(billItem.item?.purchase_price || billItem.item?.purchasePrice || itemPurchasePriceMap[id] || 0);
            saleCost += remainingQty * fallbackPrice;
          }

          if (isWithinRange) {
            rangeSoldQty[id] = (rangeSoldQty[id] || 0) + qty;
            rangeGrossSalesAmount[id] = (rangeGrossSalesAmount[id] || 0) + total;

            const extraDisc = parseFloat(bill.extraDiscount || bill.totalDiscount || bill.total_discount || 0);
            let itemShareDiscount = 0;
            if (billSubTotal > 0 && extraDisc > 0) {
              itemShareDiscount = extraDisc * (total / billSubTotal);
            }
            rangeNetSalesAmount[id] = (rangeNetSalesAmount[id] || 0) + (total - itemShareDiscount);
            rangeFifoCost[id] = (rangeFifoCost[id] || 0) + saleCost;
          }
        }
      });
    });

    const activeItemIds = new Set([
      ...Object.keys(rangeSoldQty),
      ...Object.keys(rangePurchasedQty)
    ]);

    const grouped = {};

    activeItemIds.forEach(itemId => {
      let sampleItem = null;
      for (const bill of bills) {
        const bItems = Array.isArray(bill.items) ? bill.items : [];
        for (const bi of bItems) {
          const biId = String(bi.item?.id || bi.item_id || bi.id || bi.itemName || bi.name || '');
          if (biId === itemId) {
            sampleItem = bi;
            break;
          }
        }
        if (sampleItem) break;
      }

      const itemMaster = itemsList.find(it => String(it.id || it.name) === itemId);

      const name = itemMaster?.name || sampleItem?.itemName || sampleItem?.name || sampleItem?.item?.name || 'Item';
      const unit = itemMaster?.unit || sampleItem?.item?.unit || sampleItem?.unit || (filterType === 'product' ? 'PCS' : 'NOS');

      const sold = rangeSoldQty[itemId] || 0;
      const cost = rangeFifoCost[itemId] || 0;
      const fallbackPrice = parseFloat(itemMaster?.purchase_price || itemMaster?.purchasePrice || itemPurchasePriceMap[itemId] || 0);
      const purchasePrice = sold > 0 ? (cost / sold) : fallbackPrice;

      const salesAmount = rangeNetSalesAmount[itemId] || 0;
      const grossSalesAmount = rangeGrossSalesAmount[itemId] || 0;
      const purchasedQty = rangePurchasedQty[itemId] || 0;
      const purchaseAmount = rangePurchaseAmount[itemId] || 0;
      const remainingStock = currentStocks[itemId] !== undefined ? currentStocks[itemId] : 0;
      const netProfit = filterType === 'service' ? salesAmount : (salesAmount - (sold * purchasePrice));

      grouped[itemId] = {
        id: itemId,
        name,
        unit,
        soldQty: sold,
        salesAmount,
        grossSalesAmount,
        purchasedQty,
        purchaseAmount,
        purchasePrice,
        remainingStock,
        netProfit
      };
    });

    const reportsList = Object.values(grouped);
    reportsList.sort((a, b) => b.salesAmount - a.salesAmount);

    const totalSoldQuantity = reportsList.reduce((sum, r) => sum + r.soldQty, 0);
    const grossSalesAmount = reportsList.reduce((sum, r) => sum + r.grossSalesAmount, 0);
    const netSalesAmount = reportsList.reduce((sum, r) => sum + r.salesAmount, 0);
    const totalExtraDiscount = grossSalesAmount - netSalesAmount;
    const netProfitAmount = reportsList.reduce((sum, r) => sum + r.netProfit, 0);

    return {
      reportsList,
      totalSoldQuantity,
      grossSalesAmount,
      totalExtraDiscount,
      netSalesAmount,
      netProfitAmount
    };
  }, [bills, itemsList, fromDate, toDate, activeTab, itemTypeMap, itemPurchasePriceMap]);

  const handleDownloadPdf = () => {
    window.print();
  };

  const handleShare = async () => {
    const bizName = businessProfile?.business_name || localStorage.getItem("business_name") || "Moxpal Khata";
    const reportText = `*${t(LangKeys.item_sales_report)} - ${bizName}*\n` +
      `---------------------------\n` +
      `*${t(LangKeys.date_range)}:* ${formatDate(fromDate)} to ${formatDate(toDate)}\n` +
      `*${t(LangKeys.type)}:* ${activeTab === 'product' ? t(LangKeys.products) : t(LangKeys.services)}\n` +
      `*${t(LangKeys.total_qty_sold)}:* ${reportData.totalSoldQuantity} ${activeTab === 'product' ? t(LangKeys.units) : t(LangKeys.nos)}\n` +
      `*${t(LangKeys.total_sales)}:* ${formatCurrency(reportData.grossSalesAmount)}\n` +
      (reportData.totalExtraDiscount > 0.01 ? `*${t(LangKeys.net_sale)}:* ${formatCurrency(reportData.netSalesAmount)}\n` : '') +
      `*${reportData.netProfitAmount >= 0 ? t(LangKeys.net_profit) : t(LangKeys.net_loss)}:* ${formatCurrency(reportData.netProfitAmount)}\n` +
      `---------------------------\n` +
      `Generated via Moxpal Khata`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: t(LangKeys.item_sales_report),
          text: reportText,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing report:', error);
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(reportText);
        alert(t(LangKeys.report_copied_clipboard));
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <div className="print-main-wrapper" style={styles.container}>
      {/* App Bar / Header with Integrated TabBar */}
      <div className="no-print" style={styles.header}>
        <div style={styles.headerTop}>
          <button onClick={onBack} className="no-print" style={styles.backBtn}>
            <ArrowLeft size={20} />
          </button>
          <h3 style={styles.title}>
            {t(LangKeys.item_sales_report)}
          </h3>
          <div style={{ width: '36px' }} />
        </div>

        {/* TabBar (Products / Services with clean indicator) */}
        <div style={styles.tabBar}>
          <button
            style={{
              ...styles.tabItem,
              ...(activeTab === 'product' ? styles.tabItemActive : {})
            }}
            onClick={() => setActiveTab('product')}
          >
            <Package size={16} />
            <span>{t(LangKeys.products)}</span>
          </button>
          <button
            style={{
              ...styles.tabItem,
              ...(activeTab === 'service' ? styles.tabItemActive : {})
            }}
            onClick={() => setActiveTab('service')}
          >
            <Zap size={16} />
            <span>{t(LangKeys.services)}</span>
          </button>
        </div>
      </div>

      <div className="no-print" style={styles.scrollContent}>
        {/* 1. Date Filter Card */}
        <div style={styles.filterCard}>
          <div style={styles.dateRow}>
            <div style={styles.dateChip}>
              <input
                type="date"
                value={toInputDateFormat(fromDate)}
                onChange={handleFromDateChange}
                style={styles.dateInput}
              />
            </div>
            <span style={styles.toLabel}>{t(LangKeys.to)}</span>
            <div style={styles.dateChip}>
              <input
                type="date"
                value={toInputDateFormat(toDate)}
                onChange={handleToDateChange}
                style={styles.dateInput}
              />
            </div>
          </div>
          <div className="no-print" style={styles.dropdownBox}>
            <select
              style={styles.select}
              value={selectedRange}
              onChange={(e) => applyDateRange(e.target.value)}
            >
              {dateRanges.map(r => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            <ChevronDown size={18} color="#64748B" />
          </div>
        </div>

        {isLoading ? (
          <div style={styles.loadingBox}>
            <div className="spinner-small" style={{ width: '32px', height: '32px', borderColor: '#1D4ED8', borderTopColor: 'transparent' }} />
          </div>
        ) : (
          <>
            {/* 2. Summary Metrics Card (Horizontal 4-Column Layout from Flutter) */}
            <div style={styles.summaryCard}>
              <div style={styles.summaryCardHeader}>
                {/* Total Qty Sold */}
                <div style={styles.sumItem}>
                  <span style={styles.sumLabel}>{t(LangKeys.total_qty_sold)}</span>
                  <span style={styles.sumVal}>
                    {reportData.totalSoldQuantity.toLocaleString('en-IN')} {activeTab === 'product' ? t(LangKeys.units) : t(LangKeys.nos)}
                  </span>
                </div>

                <div style={styles.sumDivider} />

                {/* Total Sales */}
                <div style={styles.sumItem}>
                  <span style={styles.sumLabel}>{t(LangKeys.total_sales)}</span>
                  <span style={{ ...styles.sumVal, color: '#2563EB' }}>
                    {formatCurrency(reportData.grossSalesAmount)}
                  </span>
                </div>

                {/* Extra Discount (If any) */}
                {reportData.totalExtraDiscount > 0.01 && (
                  <>
                    <div style={styles.sumDivider} />
                    <div style={styles.sumItem}>
                      <span style={styles.sumLabel}>{t(LangKeys.add_bill_extra_discount)}</span>
                      <span style={{ ...styles.sumVal, color: '#DC2626' }}>
                        -{formatCurrency(reportData.totalExtraDiscount)}
                      </span>
                    </div>
                  </>
                )}

                <div style={styles.sumDivider} />

                {/* Net Profit / Net Loss */}
                <div style={styles.sumItem}>
                  <span style={styles.sumLabel}>
                    {reportData.totalExtraDiscount > 0.01
                      ? (reportData.netProfitAmount >= 0 ? t(LangKeys.net_profit) : t(LangKeys.net_loss))
                      : (reportData.netProfitAmount >= 0 ? t(LangKeys.profit) : t(LangKeys.loss))}
                  </span>
                  <span
                    style={{
                      ...styles.sumVal,
                      color: reportData.netProfitAmount >= 0 ? '#16A34A' : '#DC2626'
                    }}
                  >
                    {formatCurrency(Math.abs(reportData.netProfitAmount))}
                  </span>
                </div>
              </div>

              {/* Sub-bar if discount applied */}
              {reportData.totalExtraDiscount > 0.01 && (
                <div style={styles.summaryCardFooter}>
                  <span>
                    {t(LangKeys.net_sale)}: {formatCurrency(reportData.netSalesAmount)}
                  </span>
                </div>
              )}
            </div>

            {/* 3. Grouped Item List (Matching Flutter Item Tiles) */}
            <div style={styles.listSection}>
              {reportData.reportsList.length === 0 ? (
                <div style={styles.emptyState}>
                  <Search size={48} color="#CBD5E1" />
                  <p style={{ fontSize: '14px', fontWeight: '600', color: '#64748B' }}>
                    {t(LangKeys.no_transactions_found_period)}
                  </p>
                </div>
              ) : (
                reportData.reportsList.map((report, idx) => (
                  <div key={idx} style={styles.txnTile}>
                    <div
                      style={{
                        ...styles.iconCircle,
                        backgroundColor: activeTab === 'product' ? '#FFF7ED' : '#FAF5FF',
                        color: activeTab === 'product' ? '#EA580C' : '#7C3AED'
                      }}
                    >
                      {activeTab === 'product' ? (
                        <Package size={22} color="#EA580C" />
                      ) : (
                        <Zap size={22} color="#7C3AED" />
                      )}
                    </div>
                    <div style={styles.txnInfo}>
                      <span style={styles.txnTitle}>{report.name}</span>
                      <div style={styles.txnMetaCol}>
                        {report.soldQty > 0 && (
                          <span style={styles.metaRow}>
                            {t(LangKeys.sold)}: <strong>{report.soldQty.toLocaleString('en-IN')} {report.unit}</strong>
                          </span>
                        )}
                        {report.purchasedQty > 0 && (
                          <span style={styles.metaRow}>
                            {t(LangKeys.bought)}: <strong>{report.purchasedQty.toLocaleString('en-IN')} {report.unit}</strong>
                          </span>
                        )}
                        {activeTab === 'product' && (
                          <span style={{ ...styles.metaRow, color: '#1D4ED8', fontWeight: '700' }}>
                            {t(LangKeys.current_stock)}: {report.remainingStock.toLocaleString('en-IN')} {report.unit}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={styles.txnRight}>
                      <span style={styles.txnAmount}>{formatCurrency(report.salesAmount)}</span>
                      {(activeTab === 'service' || report.purchasePrice > 0) && (
                        <span
                          style={{
                            ...styles.txnProfit,
                            color: report.netProfit >= 0 ? '#16A34A' : '#DC2626'
                          }}
                        >
                          {report.netProfit >= 0 ? `${t(LangKeys.profit)}: +` : `${t(LangKeys.loss)}: -`}
                          {formatCurrency(Math.abs(report.netProfit))}
                        </span>
                      )}
                      {report.purchaseAmount > 0 && (
                        <span style={styles.txnPurchaseAmount}>
                          {t(LangKeys.bought)}: {formatCurrency(report.purchaseAmount)}
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>

      {/* 4. Bottom Actions Bar */}
      {!isLoading && reportData.reportsList.length > 0 && (
        <div className="no-print" style={styles.bottomBar}>
          <button onClick={handleDownloadPdf} style={styles.downloadBtn}>
            <Download size={18} />
            <span>{t(LangKeys.download_pdf)}</span>
          </button>
          <button onClick={handleShare} style={styles.shareBtn}>
            <Share2 size={18} />
            <span>{t(LangKeys.share)}</span>
          </button>
        </div>
      )}

      {/* Printable Report Sheet */}
      <div className="print-only" id="item-report-pdf-template" style={{
        padding: '10mm 15mm',
        boxSizing: 'border-box',
        background: 'white',
        fontFamily: "'Outfit', 'Hind', sans-serif",
        color: '#1e293b',
        textAlign: 'left',
        width: '100%'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div style={{ width: '60px', height: '60px', display: 'flex', alignItems: 'center' }}>
            {businessProfile?.logo_url && <img src={businessProfile.logo_url} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} alt="" />}
          </div>
          <div style={{ textAlign: 'center', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <img src="https://khata.moxpal.com/assets/logo.png" style={{ height: '22px', marginBottom: '6px' }} alt="Moxpal" />
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a', margin: 0 }}>{businessProfile?.business_name || t(LangKeys.my_business)}</h2>
            {businessProfile?.owner_name && <div style={{ fontSize: '12px', color: '#475569', marginTop: '4px' }}>{t(LangKeys.name)}: {businessProfile.owner_name}</div>}
            <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{t(LangKeys.mobile_no)}: {businessProfile?.mobile || ''}</div>
          </div>
          <div style={{ width: '60px', height: '60px', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
            <img src="https://khata.moxpal.com/assets/ganesh-ji.png" style={{ width: '40px', height: '40px', objectFit: 'contain' }} alt="Ganesh" />
          </div>
        </div>

        <div style={{ background: '#1e40af', color: 'white', padding: '10px', textAlign: 'center', fontWeight: 'bold', fontSize: '16px', margin: '20px 0', borderRadius: '4px', textTransform: 'uppercase' }}>
          {t(LangKeys.item_sales_report)} ({activeTab === 'product' ? t(LangKeys.products) : t(LangKeys.services)})
        </div>

        <div style={{ textAlign: 'center', fontSize: '12px', color: '#475569', marginBottom: '20px', fontWeight: '600' }}>
          ({formatDate(fromDate)} - {formatDate(toDate)})
        </div>

        <div style={{ display: 'flex', gap: '15px', marginBottom: '30px' }}>
          <div style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px', textAlign: 'center', backgroundColor: '#f8fafc' }}>
            <span style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', marginBottom: '4px', display: 'block' }}>
              {t(LangKeys.total_qty_sold)}
            </span>
            <span style={{ fontSize: '15px', fontWeight: '800', color: '#1e2937' }}>
              {reportData.totalSoldQuantity} {activeTab === 'product' ? t(LangKeys.units) : t(LangKeys.nos)}
            </span>
          </div>
          <div style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px', textAlign: 'center', backgroundColor: '#f8fafc' }}>
            <span style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', marginBottom: '4px', display: 'block' }}>
              {t(LangKeys.total_sales)}
            </span>
            <span style={{ fontSize: '15px', fontWeight: '800', color: '#2563EB' }}>
              {formatCurrency(reportData.grossSalesAmount)}
            </span>
          </div>
          {reportData.totalExtraDiscount > 0.01 && (
            <div style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px', textAlign: 'center', backgroundColor: '#f8fafc' }}>
              <span style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', marginBottom: '4px', display: 'block' }}>
                {t(LangKeys.add_bill_extra_discount)}
              </span>
              <span style={{ fontSize: '15px', fontWeight: '800', color: '#ef4444' }}>
                -{formatCurrency(reportData.totalExtraDiscount)}
              </span>
            </div>
          )}
          <div style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px', textAlign: 'center', backgroundColor: '#f8fafc' }}>
            <span style={{ fontSize: '10px', color: '#64748b', textTransform: 'uppercase', fontWeight: '700', marginBottom: '4px', display: 'block' }}>
              {reportData.netProfitAmount >= 0 ? t(LangKeys.net_profit) : t(LangKeys.net_loss)}
            </span>
            <span
              style={{
                fontSize: '15px',
                fontWeight: '800',
                color: reportData.netProfitAmount >= 0 ? '#10b981' : '#ef4444'
              }}
            >
              {formatCurrency(Math.abs(reportData.netProfitAmount))}
            </span>
          </div>
        </div>

        {reportData.totalExtraDiscount > 0.01 && (
          <div style={{ textAlign: 'center', fontSize: '12px', fontWeight: 'bold', color: '#1e40af', marginBottom: '20px' }}>
            {t(LangKeys.net_sale)}: {formatCurrency(reportData.netSalesAmount)}
          </div>
        )}

        <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#0f172a', marginBottom: '12px', borderLeft: '4px solid #1e40af', paddingLeft: '10px' }}>
          {activeTab === 'product' ? t(LangKeys.products) : t(LangKeys.services)}
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #cbd5e1', tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ border: '1px solid #cbd5e1', padding: '10px', background: '#f8fafc', fontSize: '10px', fontWeight: '800', color: '#475569', textAlign: 'left', width: '30%' }}>
                {t(LangKeys.item_name).toUpperCase()}
              </th>
              <th style={{ border: '1px solid #cbd5e1', padding: '10px', background: '#f8fafc', fontSize: '10px', fontWeight: '800', color: '#475569', textAlign: 'center', width: '12%' }}>
                {t(LangKeys.sold).toUpperCase()}
              </th>
              <th style={{ border: '1px solid #cbd5e1', padding: '10px', background: '#f8fafc', fontSize: '10px', fontWeight: '800', color: '#475569', textAlign: 'right', width: '15%' }}>
                {t(LangKeys.total_sales).toUpperCase()}
              </th>
              <th style={{ border: '1px solid #cbd5e1', padding: '10px', background: '#f8fafc', fontSize: '10px', fontWeight: '800', color: '#475569', textAlign: 'center', width: '12%' }}>
                {t(LangKeys.bought).toUpperCase()}
              </th>
              <th style={{ border: '1px solid #cbd5e1', padding: '10px', background: '#f8fafc', fontSize: '10px', fontWeight: '800', color: '#475569', textAlign: 'right', width: '16%' }}>
                {t(LangKeys.total_purchase).toUpperCase()}
              </th>
              <th style={{ border: '1px solid #cbd5e1', padding: '10px', background: '#f8fafc', fontSize: '10px', fontWeight: '800', color: '#475569', textAlign: 'right', width: '15%' }}>
                {t(LangKeys.net_profit).toUpperCase()}
              </th>
            </tr>
          </thead>
          <tbody>
            {reportData.reportsList.length > 0 ? (
              reportData.reportsList.map((report, i) => (
                <tr key={i}>
                  <td style={{ border: '1px solid #cbd5e1', padding: '10px', fontSize: '11px', color: '#334155', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {report.name}
                  </td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '10px', fontSize: '11px', color: '#334155', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {report.soldQty} {report.unit}
                  </td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '10px', fontSize: '11px', color: '#2563eb', textAlign: 'right', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {formatCurrency(report.salesAmount)}
                  </td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '10px', fontSize: '11px', color: '#334155', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {report.purchasedQty} {report.unit}
                  </td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '10px', fontSize: '11px', color: '#475569', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {formatCurrency(report.purchaseAmount)}
                  </td>
                  <td style={{ border: '1px solid #cbd5e1', padding: '10px', fontSize: '11px', color: report.netProfit >= 0 ? '#16a34a' : '#ef4444', textAlign: 'right', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {formatCurrency(report.netProfit)}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" style={{ border: '1px solid #cbd5e1', padding: '40px', fontSize: '12px', color: '#64748b', textAlign: 'center' }}>
                  {t(LangKeys.no_transactions_found_period)}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div style={{ marginTop: '50px', borderTop: '1px solid #F1F5F9', paddingTop: '20px', textAlign: 'center', fontSize: '11px', color: '#94a3b8', fontWeight: '600' }}>
          {t(LangKeys.generated_by)} | {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true })}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    background: '#F3F4F6',
    position: 'relative'
  },
  header: {
    padding: '16px 20px 0 20px',
    background: '#1D4ED8',
    color: 'white',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    boxShadow: '0 2px 8px rgba(29, 78, 216, 0.25)'
  },
  headerTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%'
  },
  backBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    border: 'none',
    background: 'rgba(255,255,255,0.15)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background 0.15s'
  },
  title: {
    fontSize: '18px',
    fontWeight: '800',
    margin: 0,
    letterSpacing: '-0.01em',
    textAlign: 'center'
  },
  tabBar: {
    display: 'flex',
    width: '100%',
    gap: '4px'
  },
  tabItem: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '12px 16px',
    background: 'transparent',
    border: 'none',
    borderBottom: '3px solid transparent',
    color: 'rgba(255, 255, 255, 0.75)',
    fontWeight: '700',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  },
  tabItemActive: {
    color: '#FFFFFF',
    borderBottomColor: '#FFFFFF'
  },
  scrollContent: {
    flex: 1,
    padding: '14px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    paddingBottom: '90px'
  },
  filterCard: {
    background: '#FFFFFF',
    padding: '16px',
    borderRadius: '18px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.05)',
    border: '1px solid #E2E8F0',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  dateRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  dateChip: {
    flex: 1,
    height: '44px',
    background: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  dateInput: {
    border: 'none',
    background: 'transparent',
    fontWeight: '600',
    fontSize: '14px',
    color: '#1F2937',
    fontFamily: 'inherit',
    outline: 'none',
    width: '100%',
    height: '100%',
    textAlign: 'center',
    cursor: 'pointer',
    padding: '0 8px'
  },
  toLabel: {
    fontSize: '13px',
    fontWeight: '700',
    color: '#6B7280'
  },
  dropdownBox: {
    background: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '14px',
    height: '44px',
    padding: '0 14px',
    display: 'flex',
    alignItems: 'center'
  },
  select: {
    flex: 1,
    border: 'none',
    background: 'transparent',
    outline: 'none',
    fontWeight: '700',
    fontSize: '14px',
    color: '#1F2937',
    cursor: 'pointer'
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    padding: '50px'
  },
  summaryCard: {
    background: '#FFFFFF',
    borderRadius: '16px',
    border: '1px solid #E2E8F0',
    padding: '14px 16px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
    display: 'flex',
    flexDirection: 'column'
  },
  summaryCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%'
  },
  sumItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1,
    gap: '4px'
  },
  sumLabel: {
    fontSize: '10.5px',
    color: '#6B7280',
    fontWeight: '700',
    textAlign: 'center',
    textTransform: 'uppercase'
  },
  sumVal: {
    fontSize: '15px',
    fontWeight: '800',
    color: '#1E293B',
    textAlign: 'center'
  },
  sumDivider: {
    width: '1px',
    height: '32px',
    backgroundColor: '#E5E7EB'
  },
  summaryCardFooter: {
    marginTop: '10px',
    paddingTop: '10px',
    borderTop: '1px solid #F1F5F9',
    display: 'flex',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '800',
    color: '#2563EB'
  },
  listSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  txnTile: {
    background: '#FFFFFF',
    padding: '14px 16px',
    borderRadius: '16px',
    border: '1px solid #E2E8F0',
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
  },
  iconCircle: {
    width: '44px',
    height: '44px',
    borderRadius: '22px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  txnInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '3px'
  },
  txnTitle: {
    fontSize: '15.5px',
    fontWeight: '700',
    color: '#1E293B'
  },
  txnMetaCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px'
  },
  metaRow: {
    fontSize: '12px',
    color: '#4B5563',
    fontWeight: '500'
  },
  txnRight: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '3px',
    flexShrink: 0
  },
  txnAmount: {
    fontSize: '16px',
    fontWeight: '800',
    color: '#2563EB'
  },
  txnProfit: {
    fontSize: '12px',
    fontWeight: '800'
  },
  txnPurchaseAmount: {
    fontSize: '11px',
    color: '#64748B',
    fontWeight: '500'
  },
  emptyState: {
    padding: '50px 20px',
    textAlign: 'center',
    color: '#94A3B8',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    background: '#FFFFFF',
    borderRadius: '18px'
  },
  bottomBar: {
    position: 'absolute',
    bottom: '14px',
    left: '14px',
    right: '14px',
    display: 'flex',
    gap: '10px',
    zIndex: 10,
    background: 'transparent'
  },
  downloadBtn: {
    flex: 1,
    height: '48px',
    borderRadius: '14px',
    border: 'none',
    backgroundColor: '#0F5BD8',
    color: '#FFFFFF',
    fontWeight: '800',
    fontSize: '14.5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(15, 91, 216, 0.25)'
  },
  shareBtn: {
    flex: 1,
    height: '48px',
    borderRadius: '14px',
    border: 'none',
    backgroundColor: '#16A34A',
    color: '#FFFFFF',
    fontWeight: '800',
    fontSize: '14.5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(22, 163, 74, 0.25)'
  }
};

export default ItemReportView;
